/**
 * ConnectGmail — empty state when Gmail is not connected.
 */

import React from 'react';
import { Button, Alert, Typography } from 'antd';
import { motion, useReducedMotion } from 'framer-motion';
import { Mail, ShieldCheck, Clock, Users, ArrowRight, Lock } from 'lucide-react';
import { FADE_UP, STAGGER_CONTAINER } from '../constants';

const { Title, Text } = Typography;

const FEATURES = [
  { icon: ShieldCheck, label: 'Metadata only', desc: 'From, To, Date — never the body.', color: '#10b981' },
  { icon: Clock, label: 'Response times', desc: 'See delays, debt, and loops.', color: '#6366f1' },
  { icon: Users, label: 'Team patterns', desc: 'Managers can read reportees.', color: '#8b5cf6' },
];

export function ConnectGmail({ onConnect, loading, error, isDark }) {
  const reduced = useReducedMotion();
  const container = reduced ? {} : STAGGER_CONTAINER;
  const item = reduced ? {} : FADE_UP;

  const ink = isDark ? '#f8fafc' : '#0f172a';
  const mute = isDark ? '#94a3b8' : '#64748b';
  const card = isDark ? 'rgba(255,255,255,0.035)' : '#ffffff';
  const line = isDark ? 'rgba(255,255,255,0.07)' : '#eceef3';
  const tile = isDark ? 'rgba(255,255,255,0.04)' : '#f8f7ff';

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 12px 8px' }}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="visible"
        style={{
          width: '100%',
          maxWidth: 720,
          textAlign: 'center',
          background: card,
          border: `1px solid ${line}`,
          borderRadius: 22,
          padding: '40px 36px 32px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: isDark
            ? '0 18px 50px rgba(0,0,0,0.28)'
            : '0 16px 48px rgba(99,102,241,0.08)',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: '-40% -20% auto',
            height: 260,
            background: isDark
              ? 'radial-gradient(ellipse at 50% 0%, rgba(99,102,241,0.22), transparent 62%)'
              : 'radial-gradient(ellipse at 50% 0%, rgba(99,102,241,0.14), transparent 62%)',
            pointerEvents: 'none',
          }}
        />

        <motion.div variants={item} style={{ position: 'relative', marginBottom: 22 }}>
          <div style={{ position: 'relative', width: 88, height: 88, margin: '0 auto 18px' }}>
            {!reduced && (
              <>
                <motion.div
                  animate={{ scale: [1, 1.28, 1], opacity: [0.35, 0, 0.35] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                  style={{
                    position: 'absolute', inset: 0, borderRadius: 26,
                    border: '1.5px solid rgba(99,102,241,0.45)',
                  }}
                />
                <motion.div
                  animate={{ scale: [1, 1.42, 1], opacity: [0.22, 0, 0.22] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: 0.45 }}
                  style={{
                    position: 'absolute', inset: 0, borderRadius: 26,
                    border: '1px solid rgba(139,92,246,0.35)',
                  }}
                />
              </>
            )}
            <motion.div
              animate={reduced ? {} : { y: [0, -4, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                width: 88, height: 88, borderRadius: 26,
                background: 'linear-gradient(145deg, #6366f1 0%, #7c3aed 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 10px 28px rgba(99,102,241,0.38)',
              }}
            >
              <Mail size={34} color="#fff" strokeWidth={1.7} />
            </motion.div>
          </div>

          <Title level={3} style={{ margin: '0 0 8px', fontWeight: 750, letterSpacing: '-0.4px', color: ink }}>
            Connect Gmail to start
          </Title>
          <Text style={{ fontSize: 14.5, lineHeight: 1.6, color: mute, maxWidth: 440, display: 'inline-block' }}>
            TeamGrid maps how you communicate — volume, speed, and follow-through — without reading a single email body.
          </Text>
        </motion.div>

        <motion.div
          variants={item}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 10,
            marginBottom: 28,
          }}
        >
          {FEATURES.map(({ icon: Icon, label, desc, color }) => (
            <motion.div
              key={label}
              whileHover={reduced ? {} : { y: -3 }}
              transition={{ duration: 0.2 }}
              style={{
                padding: '16px 12px',
                background: tile,
                border: `1px solid ${line}`,
                borderRadius: 14,
              }}
            >
              <div style={{
                width: 34, height: 34, borderRadius: 10, margin: '0 auto 10px',
                background: `${color}18`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={17} color={color} strokeWidth={2.1} />
              </div>
              <Text strong style={{ display: 'block', fontSize: 12.5, color: ink, marginBottom: 4 }}>{label}</Text>
              <Text style={{ fontSize: 11.5, lineHeight: 1.45, color: mute }}>{desc}</Text>
            </motion.div>
          ))}
        </motion.div>

        {error && (
          <motion.div variants={item} style={{ marginBottom: 14 }}>
            <Alert type="warning" message={error} showIcon style={{ borderRadius: 12, textAlign: 'left' }} />
          </motion.div>
        )}

        <motion.div variants={item}>
          <motion.div whileHover={reduced || loading ? {} : { scale: 1.015 }} whileTap={reduced || loading ? {} : { scale: 0.985 }}>
            <Button
              type="primary"
              size="large"
              loading={loading}
              onClick={onConnect}
              style={{
                width: '100%',
                maxWidth: 360,
                height: 50,
                borderRadius: 12,
                fontWeight: 650,
                fontSize: 15,
                background: 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
                border: 'none',
                boxShadow: '0 8px 22px rgba(99,102,241,0.32)',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Mail size={16} />
                Connect Gmail
                {!loading && <ArrowRight size={15} />}
              </span>
            </Button>
          </motion.div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 14 }}>
            <Lock size={11} color={mute} />
            <Text style={{ fontSize: 12, color: mute }}>
              One-time connect. Tokens stay on TeamGrid — never in your browser.
            </Text>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
